import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { PackageCheck } from 'lucide-react';
import { AcaoPrimaria } from '../../componentes/acao-primaria';
import { Botao, Selo } from '../../componentes/base';
import { Vazio } from '../../componentes/estados';
import { CampoControlado, Selecao } from '../../componentes/formulario';
import { Modal } from '../../componentes/sobreposicao';
import { aceitoDaUrl, uuidDaUrl } from '../../componentes/filtros-lista';
import { filtrosNaUrl } from '../../componentes/filtros-visao';
import { Tabela, type Coluna } from '../../componentes/tabela';
import { data, dinheiro, nomeParceiro } from '../../nucleo/formato';
import { AvisoModo, useSessaoFluxo } from './AvisoModo';
import { DetalheRecebimento } from './DetalheRecebimento';
import { ModalRecebimento } from './ModalRecebimento';
import type { Recebimento } from './tipos';

/** Aba Recebimentos (M10.3): cada chegada de mercadoria conferida com o pedido, com a entrada no Estoque e as contas a pagar. */
export function Recebimentos() {
  const { t } = useTranslation('compras');
  const { t: tn } = useTranslation();
  // Fase 2 (W8): no modo simplificado (ou sem Estoque, no recebimento) as ações do fluxo detalhado somem; a leitura fica.
  const s = useSessaoFluxo();
  const [busca, setBusca] = useSearchParams();
  // Review r29 robustez (O2): `?recebimento=` que não é uuid é ignorado (a ficha não abre com erro).
  const [aberto, setAberto] = useState<string | null>(uuidDaUrl(busca.get('recebimento')) || null);
  useEffect(() => {
    const id = uuidDaUrl(busca.get('recebimento'));
    if (id) setAberto(id);
  }, [busca]);
  const [escolhendo, setEscolhendo] = useState(false);
  const [pedidoEscolhido, setPedidoEscolhido] = useState<string | null>(null);
  const [pedidoReceber, setPedidoReceber] = useState<string | null>(null);
  // Review r29 robustez (B2, varredura): `?divergente=` que a API recusaria (`sim`, `x`) é ignorado; antes a lista caía em
  // "Indisponível". O filtro só grava `true`.
  const divergente = aceitoDaUrl(busca.get('divergente'), ['true']);

  const colunas: Coluna<Recebimento>[] = [
    { chave: 'numero', rotulo: t('campos.numero'), mono: true, ordenavel: false, render: (r) => <span className="font-medium">{r.numero}</span> },
    {
      chave: 'pedido_numero',
      rotulo: t('campos.pedido'),
      principal: true,
      render: (r) => (
        <span className="flex min-w-0 max-w-sm flex-col">
          <span className="numero truncate" title={r.pedido_numero}>{r.pedido_numero}</span>
          <span className="truncate text-xs text-tinta-3" title={nomeParceiro(r.fornecedor_nome)}>{nomeParceiro(r.fornecedor_nome)}</span>
        </span>
      ),
    },
    { chave: 'data', rotulo: t('campos.dataRecebimento'), ordenavel: true, mono: true, render: (r) => data(r.data) },
    { chave: 'itens', rotulo: t('campos.itens'), alinhar: 'direita', mono: true, render: (r) => r.itens },
    { chave: 'total', rotulo: t('campos.total'), ordenavel: true, alinhar: 'direita', mono: true, render: (r) => dinheiro(r.total_centavos, r.moeda) },
    { chave: 'contas', rotulo: t('campos.contasAPagar'), alinhar: 'direita', mono: true, render: (r) => r.documentos.length },
    { chave: 'deposito_nome', rotulo: t('campos.deposito'), oculta: true, render: (r) => r.deposito_nome ?? '' },
    {
      chave: 'conferencia',
      rotulo: t('campos.conferencia'),
      render: (r) => <Selo cor={r.divergente ? 'laranja' : 'verde'}>{r.divergente ? t('recebimento.comDivergencia') : t('recebimento.conferidoSelo')}</Selo>,
    },
  ];

  return (
    <div className="flex min-w-0 flex-col gap-4">
      <AvisoModo recebimento />
      <AcaoPrimaria>
        {s.pode('compras.receber') && (
          <Botao variante="primario" icone={<PackageCheck className="size-4" />} onClick={() => setEscolhendo(true)}>
            {t('acoes.registrarRecebimento')}
          </Botao>
        )}
      </AcaoPrimaria>

      <Tabela<Recebimento>
        tela="compras.recebimentos"
        caminho="/compras/recebimentos"
        params={{ divergente: divergente || undefined }}
        filtros={{ divergente }}
        aoAplicarFiltros={(f) => setBusca((atual) => filtrosNaUrl(atual, f), { replace: true })}
        colunas={colunas}
        aoClicar={(r) => setAberto(r.id)}
        filtrosExtra={
          <Selecao
            aria-label={t('campos.conferencia')}
            vazio={t('filtros.todosRecebimentos')}
            opcoes={[{ valor: 'true', rotulo: t('filtros.soDivergentes') }]}
            value={divergente}
            onChange={(e) =>
              setBusca(
                (atual) => {
                  const n = new URLSearchParams(atual);
                  if (e.target.value) n.set('divergente', e.target.value);
                  else n.delete('divergente');
                  return n;
                },
                { replace: true },
              )
            }
          />
        }
        vazio={<Vazio texto={t('vazio.recebimentos')} />}
      />

      <Modal
        aberto={escolhendo}
        aoFechar={() => setEscolhendo(false)}
        titulo={t('recebimento.escolherPedidoTitulo')}
        rodape={
          <>
            <Botao variante="fantasma" onClick={() => setEscolhendo(false)}>
              {tn('comum.cancelar')}
            </Botao>
            <Botao
              variante="primario"
              disabled={!pedidoEscolhido}
              onClick={() => {
                setPedidoReceber(pedidoEscolhido);
                setEscolhendo(false);
              }}
            >
              {tn('comum.confirmar')}
            </Botao>
          </>
        }
      >
        <div className="min-h-72">
        <p className="mb-3 text-sm text-tinta-2">{t('recebimento.escolherPedidoTexto')}</p>
        <CampoControlado
          def={{
            nome: 'pedido',
            rotulo: t('campos.pedido'),
            tipo: 'busca',
            obrigatorio: true,
            busca: { caminho: '/compras/pedidos', params: { a_receber: true }, rotulo: (i) => `${String(i.numero)} ${String(i.fornecedor_nome)}` },
          }}
          valor={pedidoEscolhido}
          aoMudar={(v) => setPedidoEscolhido((v as string | null) || null)}
        />
        </div>
      </Modal>
      <ModalRecebimento pedidoId={pedidoReceber} aoFechar={() => setPedidoReceber(null)} aoRegistrar={(id) => setAberto(id)} />
      <DetalheRecebimento
        id={aberto}
        aoFechar={() => {
          setAberto(null);
          if (busca.get('recebimento')) {
            setBusca(
              (atual) => {
                const n = new URLSearchParams(atual);
                n.delete('recebimento');
                return n;
              },
              { replace: true },
            );
          }
        }}
      />
    </div>
  );
}
